/**
 * Quotation detail page — preview + actions + revision history.
 *
 * URL: `/quotations/:quotationId`
 *
 * Layout:
 *   Header bar — back / number / status pill / value · Edit / Print /
 *                Duplicate / Send / Accept / Reject / Delete menu
 *   Left  — Inline rendered quote (the same HTML the printed
 *           PDF uses, scaled to fit the screen via an iframe so the
 *           print template can't leak its CSS into the app shell)
 *   Right rail — Status timeline + Revision history list. Each
 *                revision row click → switches the preview to show
 *                that historical snapshot (with a yellow "Viewing
 *                R{n}" banner + "Back to current" link)
 *
 * Mark-as-accepted / mark-as-rejected open small confirm pops so
 * the studio doesn't accidentally close a quote on misclick.
 *
 * Duplicate creates a fresh draft with version 0 + new number,
 * copying everything else verbatim (including line items + T&C),
 * then navigates to that new doc's edit page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { httpsCallable } from 'firebase/functions';

import { canCRMWrite } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { functions } from '@/lib/firebase';
import {
  acceptQuotation,
  compactInr,
  createQuotation,
  deleteQuotation,
  formatInr,
  markQuotationSent,
  nextQuotationNumber,
  quotationStatusTone,
  rejectQuotation,
  updateQuotation,
  type Quotation,
} from '@/lib/quotations';
import { buildQuotationHtml } from '@/lib/quotationReport';
import { openHtmlForPrint } from '@/lib/projectReports';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { useFullOrganization } from '@/lib/useFullOrganization';
import {
  useQuotation,
  useQuotationRevisions,
} from '@/lib/useQuotations';

function fmtDate(ts: { toMillis(): number } | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts.toMillis()).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtDateTime(ts: { toMillis(): number } | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts.toMillis()).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function QuotationDetailPage() {
  const navigate = useNavigate();
  const { quotationId } = useParams<{ quotationId: string }>();
  const { user } = useAuth();
  const { userDoc } = useCurrentUser();
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canCRMWrite(orgRole);

  const { quotation, loading } = useQuotation(quotationId ?? null);
  const { revisions } = useQuotationRevisions(quotationId ?? null);

  // The user can "browse" historical revisions inline. When set, the
  // preview pane renders the snapshot instead of the live doc.
  const [previewVersion, setPreviewVersion] = useState<number | null>(null);

  // Confirm states for destructive / status-change actions.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmAccept, setConfirmAccept] = useState(false);
  const [rejectPrompt, setRejectPrompt] = useState<{ open: boolean; reason: string }>(
    { open: false, reason: '' },
  );
  // Inline "Create project from this quote" form. Replaces the old
  // NewProjectModal approach — the modal's strict 3-char address
  // validation was blocking creation from quotes that didn't have
  // a client address set, with errors surfacing only as easy-to-
  // miss toasts. The Cloud Function only requires `name`, so the
  // inline form mirrors that minimal requirement.
  //
  // Shape: null = collapsed; non-null = expanded form draft.
  const [createProjectDraft, setCreateProjectDraft] = useState<
    | {
        name: string;
        siteAddress: string;
        value: string;
        startDateKey: string;
        endDateKey: string;
      }
    | null
  >(null);
  const [createProjectBusy, setCreateProjectBusy] = useState(false);
  const [createProjectError, setCreateProjectError] = useState<string | null>(
    null,
  );

  function openCreateProject() {
    if (!quotation) return;
    // Truncate the name to the Cloud Function's 80-char max so
    // back-to-back-name quotes ("Bus Driver Don Bosco Apartment
    // Floor 3 Wing C") don't fail server-side validation.
    const truncatedName = quotation.clientName
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, 80);
    const today = new Date();
    setCreateProjectDraft({
      name: truncatedName,
      siteAddress: (quotation.clientAddress ?? '').trim(),
      value: String(quotation.total || ''),
      startDateKey: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`,
      endDateKey: '',
    });
    setCreateProjectError(null);
  }

  async function handleCreateProjectFromQuote() {
    if (!quotation || !user || !orgId || !createProjectDraft) return;
    const trimmedName = createProjectDraft.name.trim();
    if (!trimmedName) {
      setCreateProjectError('Project name is required.');
      return;
    }
    if (trimmedName.length > 80) {
      setCreateProjectError(
        'Project name must be 80 characters or fewer.',
      );
      return;
    }
    const numericValue = parseFloat(createProjectDraft.value) || 0;
    if (numericValue < 0) {
      setCreateProjectError('Project value cannot be negative.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(createProjectDraft.startDateKey)) {
      setCreateProjectError('Pick a valid start date.');
      return;
    }
    const endKey = createProjectDraft.endDateKey;
    if (endKey && !/^\d{4}-\d{2}-\d{2}$/.test(endKey)) {
      setCreateProjectError('Pick a valid end date or leave it blank.');
      return;
    }
    if (endKey && endKey < createProjectDraft.startDateKey) {
      setCreateProjectError('End date must be on or after the start date.');
      return;
    }

    setCreateProjectBusy(true);
    setCreateProjectError(null);
    try {
      // The Cloud Function expects ISO strings for dates. We anchor
      // at noon to dodge UTC-edge timezone surprises (a midnight-IST
      // date sometimes comes out as the previous day in UTC, which
      // makes timelines look wrong).
      const startIso = new Date(
        `${createProjectDraft.startDateKey}T12:00:00`,
      ).toISOString();
      const endIso = endKey
        ? new Date(`${endKey}T12:00:00`).toISOString()
        : null;

      const callable = httpsCallable<
        {
          orgId: string;
          name: string;
          startDate: string;
          endDate: string | null;
          siteAddress: string;
          value: number;
          photoUri: string | null;
          status?: 'active';
          progress?: number;
        },
        { projectId: string }
      >(functions, 'createProject');
      const { data } = await callable({
        orgId,
        name: trimmedName,
        startDate: startIso,
        endDate: endIso,
        siteAddress: createProjectDraft.siteAddress.trim(),
        value: Math.round(numericValue),
        photoUri: null,
        status: 'active',
        progress: 0,
      });

      // Back-link the project id onto the quote so future visits
      // jump straight to the project from the right rail.
      try {
        await updateQuotation(quotation.id, { projectId: data.projectId });
      } catch (e) {
        console.warn('[QuotationDetail] back-link failed:', e);
      }
      navigate(`/projects/${data.projectId}`);
    } catch (e) {
      setCreateProjectError(
        (e as Error).message ||
          'Could not create the project. Please try again.',
      );
    } finally {
      setCreateProjectBusy(false);
    }
  }

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Iframe ref so we can write the print HTML into it on every change.
  const previewRef = useRef<HTMLIFrameElement | null>(null);

  // Resolve which snapshot to render — live doc OR a revision snap.
  const displayed: Quotation | null = useMemo(() => {
    if (!quotation) return null;
    if (previewVersion == null) return quotation;
    const rev = revisions.find((r) => r.version === previewVersion);
    return rev?.snapshot ?? quotation;
  }, [quotation, previewVersion, revisions]);

  // Re-render preview iframe whenever the displayed snapshot changes.
  useEffect(() => {
    if (!displayed) return;
    const iframe = previewRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(buildQuotationHtml(displayed));
    doc.close();
  }, [displayed]);

  // ── Actions ────────────────────────────────────────────────

  function handlePrint() {
    if (!displayed) return;
    openHtmlForPrint(
      buildQuotationHtml(displayed),
      `${displayed.number} — ${displayed.clientName}`,
    );
  }

  async function handleMarkSent() {
    if (!quotation) return;
    setBusy(true);
    setError(null);
    try {
      await markQuotationSent(quotation.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleAccept() {
    if (!quotation || !user) return;
    setBusy(true);
    setError(null);
    try {
      await acceptQuotation({ id: quotation.id, acceptedBy: user.uid });
      setConfirmAccept(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    if (!quotation) return;
    if (!rejectPrompt.reason.trim()) {
      setError('Please add a short reason so the team has context.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await rejectQuotation({
        id: quotation.id,
        reason: rejectPrompt.reason,
      });
      setRejectPrompt({ open: false, reason: '' });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!quotation) return;
    setBusy(true);
    setError(null);
    try {
      await deleteQuotation(quotation.id);
      navigate('/quotations');
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  async function handleDuplicate() {
    if (!quotation || !orgId || !user) return;
    setBusy(true);
    setError(null);
    try {
      const number = await nextQuotationNumber(orgId);
      const createdByName =
        ((userDoc?.data?.displayName as string | undefined) ?? '').trim() ||
        user.email ||
        'You';
      const {
        id: _ignoreId,
        version: _ignoreVer,
        status: _ignoreStatus,
        acceptedAt: _ignoreAccAt,
        acceptedBy: _ignoreAccBy,
        rejectedAt: _ignoreRejAt,
        rejectionReason: _ignoreRejReason,
        createdAt: _ignoreCreatedAt,
        updatedAt: _ignoreUpdatedAt,
        ...rest
      } = quotation;
      const newId = await createQuotation({
        ...rest,
        number,
        status: 'draft',
        createdBy: user.uid,
        createdByName,
      });
      navigate(`/quotations/${newId}/edit`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // ── UI ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          Loading quotation…
        </div>
      </div>
    );
  }
  if (!quotation) {
    return (
      <div className="page active" style={{ display: 'block' }}>
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.4 }}>
            <i className="ph ph-file-x" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
            Quotation not found
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/quotations')}
          >
            Back to Quotations
          </button>
        </div>
      </div>
    );
  }

  const tone = quotationStatusTone(quotation.status);
  const onRevisionView = previewVersion != null && previewVersion !== quotation.version;

  return (
    <div
      className="page active"
      id="page-quotation-detail"
      style={{ display: 'block' }}
    >
      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          paddingBottom: 16,
          marginBottom: 16,
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate('/quotations')}
          style={{ padding: '6px 12px' }}
        >
          <i className="ph ph-arrow-left" /> Back
        </button>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flex: 1, minWidth: 200 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 800,
              fontFamily: 'Menlo, monospace',
              letterSpacing: 0.3,
            }}
          >
            {quotation.number}
          </h1>
          {quotation.version > 0 ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 999,
                background: '#F1F5F9',
                color: '#475569',
              }}
            >
              R{quotation.version}
            </span>
          ) : null}
          <span
            className="stage-pill"
            style={{
              background: tone.bg,
              color: tone.fg,
              fontWeight: 700,
              fontSize: 10,
              padding: '2px 8px',
            }}
          >
            {quotation.status.toUpperCase()}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {quotation.clientName}
          </span>
          <span
            style={{
              fontSize: 13,
              color: 'var(--text-primary)',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              marginLeft: 'auto',
            }}
          >
            {compactInr(quotation.total)}
          </span>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={handlePrint}
            disabled={busy}
            title="Print / save as PDF"
          >
            <i className="ph ph-printer" /> Print
          </button>
          {canWrite && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/quotations/${quotation.id}/edit`)}
              disabled={busy}
            >
              <i className="ph ph-pencil-simple" /> Edit
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              className="btn-secondary"
              onClick={handleDuplicate}
              disabled={busy}
            >
              <i className="ph ph-copy" /> Duplicate
            </button>
          )}
          {canWrite && quotation.status === 'draft' ? (
            <button
              type="button"
              className="btn-primary"
              onClick={handleMarkSent}
              disabled={busy}
            >
              <i className="ph ph-paper-plane-tilt" /> Mark sent
            </button>
          ) : null}
          {canWrite && quotation.status === 'sent' ? (
            <>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setConfirmAccept(true)}
                disabled={busy}
                style={{
                  background: '#15803D',
                  borderColor: '#15803D',
                }}
              >
                <i className="ph ph-check-circle" /> Mark accepted
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setRejectPrompt({ open: true, reason: '' })}
                disabled={busy}
                style={{ color: 'var(--critical)' }}
              >
                <i className="ph ph-x-circle" /> Reject
              </button>
            </>
          ) : null}
          {canWrite && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              style={{ color: 'var(--critical)' }}
              title="Delete quotation"
            >
              <i className="ph ph-trash" />
            </button>
          )}
        </div>
      </div>

      {/* Errors / inline confirms */}
      {error ? (
        <div
          className="card"
          style={{
            padding: 12,
            background: 'var(--critical-bg)',
            color: 'var(--critical)',
            border: '1px solid #FECACA',
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 13,
          }}
        >
          <span>{error}</span>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setError(null)}
            style={{ fontSize: 12 }}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* Accepted-quote → Project conversion banner.
          Shown ONLY when the quote is accepted AND not yet
          linked to a project. One click opens the New Project
          modal pre-filled with the quote's client name + total
          so the studio can spin up the project in <30 seconds. */}
      {quotation.status === 'accepted' && !quotation.projectId ? (
        <div
          className="card"
          style={{
            padding: 0,
            marginBottom: 16,
            background: '#F0FDF4',
            border: '1px solid #BBF7D0',
            overflow: 'hidden',
          }}
        >
          {/* Collapsed CTA — single row that becomes the expanded
              form on click. Inline (not a modal) so validation and
              errors stay visible without any toasts that could be
              missed. */}
          {createProjectDraft == null ? (
            <div
              style={{
                padding: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <i
                className="ph-fill ph-check-circle"
                style={{ fontSize: 18, color: '#15803D' }}
              />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#15803D',
                  }}
                >
                  Quote accepted — ready to kick off the project
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    marginTop: 2,
                  }}
                >
                  Create the project from this quote — the client
                  name and contract value carry over automatically.
                </div>
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={openCreateProject}
                style={{
                  background: '#15803D',
                  borderColor: '#15803D',
                }}
              >
                <i className="ph-bold ph-folder-plus" /> Create project from
                quote
              </button>
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  marginBottom: 12,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#15803D',
                    }}
                  >
                    Create project from quote
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    Defaults pulled from the quote — tweak anything
                    before creating.
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setCreateProjectDraft(null)}
                  disabled={createProjectBusy}
                  style={{ fontSize: 12 }}
                >
                  Cancel
                </button>
              </div>

              {createProjectError ? (
                <div
                  style={{
                    padding: 10,
                    marginBottom: 12,
                    background: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: 6,
                    color: 'var(--critical)',
                    fontSize: 13,
                  }}
                >
                  {createProjectError}
                </div>
              ) : null}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr',
                  gap: 12,
                }}
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--text-tertiary)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      marginBottom: 6,
                    }}
                  >
                    Project name *
                  </label>
                  <input
                    style={projectInput}
                    value={createProjectDraft.name}
                    onChange={(e) =>
                      setCreateProjectDraft((d) =>
                        d ? { ...d, name: e.currentTarget.value } : d,
                      )
                    }
                    maxLength={80}
                    placeholder="e.g. Sharma Residence"
                    autoFocus
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--text-tertiary)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      marginBottom: 6,
                    }}
                  >
                    Project value (₹)
                  </label>
                  <input
                    type="number"
                    style={projectInput}
                    value={createProjectDraft.value}
                    onChange={(e) =>
                      setCreateProjectDraft((d) =>
                        d ? { ...d, value: e.currentTarget.value } : d,
                      )
                    }
                    min={0}
                    step={1}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--text-tertiary)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      marginBottom: 6,
                    }}
                  >
                    Start date *
                  </label>
                  <input
                    type="date"
                    style={projectInput}
                    value={createProjectDraft.startDateKey}
                    onChange={(e) =>
                      setCreateProjectDraft((d) =>
                        d
                          ? { ...d, startDateKey: e.currentTarget.value }
                          : d,
                      )
                    }
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--text-tertiary)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      marginBottom: 6,
                    }}
                  >
                    End date (optional)
                  </label>
                  <input
                    type="date"
                    style={projectInput}
                    value={createProjectDraft.endDateKey}
                    onChange={(e) =>
                      setCreateProjectDraft((d) =>
                        d ? { ...d, endDateKey: e.currentTarget.value } : d,
                      )
                    }
                  />
                </div>
                <div style={{ gridColumn: 'span 4' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--text-tertiary)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      marginBottom: 6,
                    }}
                  >
                    Site address (optional)
                  </label>
                  <input
                    style={projectInput}
                    value={createProjectDraft.siteAddress}
                    onChange={(e) =>
                      setCreateProjectDraft((d) =>
                        d
                          ? { ...d, siteAddress: e.currentTarget.value }
                          : d,
                      )
                    }
                    placeholder="Where the team will work (can be added later)"
                  />
                </div>
              </div>

              <div
                style={{
                  marginTop: 14,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setCreateProjectDraft(null)}
                  disabled={createProjectBusy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleCreateProjectFromQuote}
                  disabled={createProjectBusy}
                  style={{
                    background: '#15803D',
                    borderColor: '#15803D',
                  }}
                >
                  {createProjectBusy ? (
                    'Creating…'
                  ) : (
                    <>
                      <i className="ph-bold ph-folder-plus" /> Create project
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Snapshot banner */}
      {onRevisionView ? (
        <div
          style={{
            padding: '10px 14px',
            background: '#FEF3C7',
            border: '1px solid #FDE68A',
            borderRadius: 8,
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            fontSize: 13,
          }}
        >
          <span style={{ color: '#92400E' }}>
            <i className="ph-bold ph-info" style={{ marginRight: 6 }} />
            Viewing historical revision R{previewVersion}. The current
            version is R{quotation.version}.
          </span>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setPreviewVersion(null)}
            style={{ fontSize: 12 }}
          >
            ← Back to current
          </button>
        </div>
      ) : null}

      {confirmAccept ? (
        <ConfirmBanner
          tone="success"
          text="Mark this quote as accepted? Today's date will be stamped."
          onCancel={() => setConfirmAccept(false)}
          onConfirm={handleAccept}
          confirmLabel="Yes, accept"
          busy={busy}
        />
      ) : null}

      {rejectPrompt.open ? (
        <div
          className="card"
          style={{
            padding: 12,
            marginBottom: 16,
            border: '1px solid #FECACA',
            background: '#FEF2F2',
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ color: 'var(--critical)', fontSize: 13, fontWeight: 600 }}>
            Mark as rejected:
          </span>
          <input
            style={{
              flex: 1,
              minWidth: 200,
              padding: '6px 10px',
              border: '1px solid #FECACA',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
            placeholder="Reason (shown in audit + history)"
            value={rejectPrompt.reason}
            onChange={(e) =>
              setRejectPrompt((p) => ({ ...p, reason: e.currentTarget.value }))
            }
            autoFocus
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setRejectPrompt({ open: false, reason: '' })}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleReject}
            disabled={busy}
            style={{
              background: 'var(--critical)',
              borderColor: 'var(--critical)',
            }}
          >
            {busy ? 'Saving…' : 'Confirm reject'}
          </button>
        </div>
      ) : null}

      {confirmDelete ? (
        <ConfirmBanner
          tone="critical"
          text="Delete this quotation permanently? The revision history will be orphaned."
          onCancel={() => setConfirmDelete(false)}
          onConfirm={handleDelete}
          confirmLabel="Yes, delete"
          busy={busy}
        />
      ) : null}

      {/* Main 2-col layout */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 320px',
          gap: 24,
          alignItems: 'flex-start',
        }}
      >
        {/* Preview iframe */}
        <div
          className="card"
          style={{ padding: 0, overflow: 'hidden' }}
        >
          <iframe
            ref={previewRef}
            title="Quotation preview"
            style={{
              width: '100%',
              height: '85vh',
              border: 'none',
              display: 'block',
              background: '#fff',
            }}
          />
        </div>

        {/* Right rail */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            position: 'sticky',
            top: 16,
            alignSelf: 'flex-start',
          }}
        >
          {/* Summary card */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              className="card-header"
              style={{
                background: '#F9FAFB',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div className="card-title" style={{ fontSize: 12 }}>
                QUOTE SUMMARY
              </div>
            </div>
            <div style={{ padding: 14 }}>
              <SummaryRow label="Date" value={fmtDate(quotation.date)} />
              <SummaryRow label="Valid until" value={fmtDate(quotation.validUntil)} />
              {quotation.projectId ? (
                <div
                  style={{
                    marginTop: 8,
                    padding: '6px 10px',
                    background: 'var(--brand-bg)',
                    border: '1px solid var(--brand)',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--brand)',
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/projects/${quotation.projectId}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/projects/${quotation.projectId}`);
                  }}
                >
                  <i className="ph-fill ph-folder" />
                  Project linked · view
                  <i
                    className="ph ph-arrow-up-right"
                    style={{ marginLeft: 'auto' }}
                  />
                </div>
              ) : null}
              <SummaryRow label="Subtotal" value={formatInr(quotation.subtotal)} />
              {quotation.discountAmount > 0 ? (
                <SummaryRow
                  label="Discount"
                  value={`− ${formatInr(quotation.discountAmount)}`}
                />
              ) : null}
              {quotation.gst.enabled ? (
                <SummaryRow
                  label={`GST (${quotation.gst.rate}%)`}
                  value={formatInr(quotation.taxAmount)}
                />
              ) : null}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: '1px solid var(--border)',
                  fontSize: 14,
                  fontWeight: 800,
                }}
              >
                <span>Grand total</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatInr(quotation.total)}
                </span>
              </div>
            </div>
          </div>

          {/* Status timeline */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              className="card-header"
              style={{
                background: '#F9FAFB',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div className="card-title" style={{ fontSize: 12 }}>
                STATUS TIMELINE
              </div>
            </div>
            <div style={{ padding: 14, fontSize: 13 }}>
              <TimelineRow
                label="Created"
                date={fmtDateTime(quotation.createdAt)}
                meta={quotation.createdByName ?? 'Studio'}
              />
              {quotation.status !== 'draft' ? (
                <TimelineRow
                  label="Sent"
                  date={fmtDateTime(quotation.updatedAt)}
                  meta={`Current version R${quotation.version}`}
                />
              ) : null}
              {quotation.status === 'accepted' && quotation.acceptedAt ? (
                <TimelineRow
                  label="Accepted"
                  date={fmtDateTime(quotation.acceptedAt)}
                  meta="Client approval recorded"
                  tone="#15803D"
                />
              ) : null}
              {quotation.status === 'rejected' && quotation.rejectedAt ? (
                <TimelineRow
                  label="Rejected"
                  date={fmtDateTime(quotation.rejectedAt)}
                  meta={quotation.rejectionReason ?? '—'}
                  tone="var(--critical)"
                />
              ) : null}
              {quotation.status === 'expired' ? (
                <TimelineRow
                  label="Expired"
                  date={fmtDate(quotation.validUntil)}
                  meta="Validity lapsed"
                  tone="var(--warning)"
                />
              ) : null}
            </div>
          </div>

          {/* Revision history */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              className="card-header"
              style={{
                background: '#F9FAFB',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div className="card-title" style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>REVISION HISTORY</span>
                <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                  {revisions.length} snapshot{revisions.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              <RevisionItem
                version={quotation.version}
                isCurrent
                isViewing={previewVersion == null}
                onView={() => setPreviewVersion(null)}
                title={
                  quotation.version === 0
                    ? 'Initial version'
                    : `Current — R${quotation.version}`
                }
                date={fmtDateTime(quotation.updatedAt)}
              />
              {revisions.map((r) => (
                <RevisionItem
                  key={r.id}
                  version={r.version}
                  isCurrent={false}
                  isViewing={previewVersion === r.version}
                  onView={() => setPreviewVersion(r.version)}
                  title={r.note}
                  date={fmtDateTime(r.createdAt)}
                  meta={r.createdByName}
                />
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* The old NewProjectModal mount was removed — the green
          "Create project from quote" banner now expands inline
          and calls the createProject callable directly. See
          openCreateProject() + handleCreateProjectFromQuote() at
          the top of this component. */}
    </div>
  );
}

// Inline-form style helper — single source of truth for the four
// inputs inside the green "Create project from quote" panel.
const projectInput: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  outline: 'none',
};

// ───── Small helpers ─────

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        fontSize: 12,
        color: 'var(--text-secondary)',
      }}
    >
      <span>{label}</span>
      <span
        style={{
          color: 'var(--text-primary)',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function TimelineRow({
  label,
  date,
  meta,
  tone = 'var(--text-primary)',
}: {
  label: string;
  date: string;
  meta?: string;
  tone?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: '6px 0',
      }}
    >
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: 3,
          background: tone,
          marginTop: 7,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: tone }}>
          {label}
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {date}
        </div>
        {meta ? (
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              marginTop: 2,
            }}
          >
            {meta}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RevisionItem({
  version,
  isCurrent,
  isViewing,
  onView,
  title,
  date,
  meta,
}: {
  version: number;
  isCurrent: boolean;
  isViewing: boolean;
  onView: () => void;
  title: string;
  date: string;
  meta?: string;
}) {
  return (
    <li
      onClick={onView}
      style={{
        padding: '10px 14px',
        borderTop: '1px solid var(--border)',
        cursor: 'pointer',
        background: isViewing ? 'var(--brand-bg)' : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
          alignItems: 'baseline',
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: isViewing ? 'var(--brand)' : 'var(--text-primary)',
          }}
        >
          R{version}
          {isCurrent ? ' · Current' : ''}
        </span>
        <span
          style={{
            fontSize: 10,
            color: 'var(--text-tertiary)',
            fontFamily: 'Menlo, monospace',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {date}
        </span>
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          marginTop: 4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </div>
      {meta ? (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            marginTop: 2,
          }}
        >
          {meta}
        </div>
      ) : null}
    </li>
  );
}

function ConfirmBanner({
  tone,
  text,
  onCancel,
  onConfirm,
  confirmLabel,
  busy,
}: {
  tone: 'success' | 'critical';
  text: string;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  busy: boolean;
}) {
  const bg = tone === 'critical' ? '#FEF2F2' : '#F0FDF4';
  const border = tone === 'critical' ? '#FECACA' : '#BBF7D0';
  const accent = tone === 'critical' ? 'var(--critical)' : '#15803D';
  return (
    <div
      className="card"
      style={{
        padding: 12,
        marginBottom: 16,
        background: bg,
        border: `1px solid ${border}`,
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{text}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onConfirm}
          disabled={busy}
          style={{ background: accent, borderColor: accent }}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}
